import { Client } from "@/models/Client";
import { baseApi } from "./base-api";
import { ApiEndpoints, buildEndpoint } from "@_constants/endpoints";
import {
  Dashboard,
  CreateDashboardRequest,
  DashboardStateFilter,
  DashboardClientsResponse,
  TabBulkUpdateItem,
} from "@models/Dashboards";
import { DashboardLayoutData } from "@/utils/dashboard-storage";
import {
  RevokeDashboardRequest,
  RevokeDashboardResponse,
  ShareDashboardRequest,
  ShareDashboardResponse,
} from "@/models/User";
import { DashboardFiltersResponse } from "@/models/Filter";
import { TabLayoutResponse } from "@/models/Widget";
import type {
  ValidateGuardrailsRequest,
  ValidateGuardrailsResponse,
} from "@models/Dataset";

export const dashboardApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDashboards: builder.query<Dashboard[], { state?: DashboardStateFilter }>(
      {
        query: () => ({
          url: ApiEndpoints.DASHBOARD_DASHBOARDS_LIST,
        }),
        keepUnusedDataFor: 0,
        providesTags: ["Dashboard"],
        transformResponse: (response: any[]): Dashboard[] => {
          return response;
        },
      },
    ),
    createDashboard: builder.mutation<Dashboard, CreateDashboardRequest>({
      query: ({ ignoreDuplicateNames, ...body }) => ({
        url: ApiEndpoints.DASHBOARD_DASHBOARDS,
        method: "POST",
        params: { ignoreDuplicateNames },
        body: {
          name: body.name,
          primaryClientId: body.primaryClientId,
          additionalClientIds: body.additionalClientIds,
        },
      }),
      invalidatesTags: (_, error) => {
        return error ? [] : ["Dashboard"];
      },
    }),
    getDashboardById: builder.query<Dashboard, string>({
      query: (dashboardId) => ({
        url: buildEndpoint(ApiEndpoints.DASHBOARD_BY_ID, { dashboardId }),
      }),
      providesTags: (_, __, id) => [{ type: "Dashboard", id }],
      keepUnusedDataFor: 0,
    }),

    getClients: builder.query<Client[], void>({
      query: () => ({
        url: ApiEndpoints.DASHBOARD_CLIENTS,
        method: "GET",
      }),
    }),

    getDashboardClients: builder.query<DashboardClientsResponse, string>({
      query: (dashboardId) => ({
        url: buildEndpoint(ApiEndpoints.DASHBOARD_CLIENTS_BY_ID, {
          dashboardId,
        }),
      }),
    }),

    shareDashboardWithUsers: builder.mutation<
      ShareDashboardResponse,
      { dashboardId: string; body: ShareDashboardRequest }
    >({
      query: ({ dashboardId, body }) => ({
        url: buildEndpoint(ApiEndpoints.SHARE_DASHBOARD_WITH_CLIENTS_USERS, {
          dashboardId,
        }),
        method: "POST",
        body,
      }),
      invalidatesTags: (_, error, { dashboardId }) =>
        error ? [] : [{ type: "Dashboard", id: dashboardId }, "Dashboard"],
    }),

    revokeDashboardAccess: builder.mutation<
      RevokeDashboardResponse,
      { dashboardId: string; body: RevokeDashboardRequest }
    >({
      query: ({ dashboardId, body }) => ({
        url: buildEndpoint(
          ApiEndpoints.REVOKE_DASHBOARD_ACCESS_FOR_CLIENTS_USERS,
          {
            dashboardId,
          },
        ),
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error ? [] : [{ type: "Dashboard", id: arg.dashboardId }, "Dashboard"],
    }),

    patchDashboard: builder.mutation<
      Dashboard,
      {
        dashboardId: string;
        name?: string;
        additionalClientIds?: string[];
        ignoreDuplicateNames?: boolean;
        state?: string;
      }
    >({
      query: ({ dashboardId, ignoreDuplicateNames, ...body }) => ({
        url: buildEndpoint(ApiEndpoints.DASHBOARD_BY_ID, { dashboardId }),
        method: "PATCH",
        params: { ignoreDuplicateNames },
        body,
      }),
      invalidatesTags: (_, error, { dashboardId }) =>
        error ? [] : [{ type: "Dashboard", id: dashboardId }, "Dashboard"],
    }),

    duplicateDashboard: builder.mutation<
      Dashboard,
      {
        dashboardId: string;
        name: string;
        primaryClientId: string;
        additionalClientIds?: string[];
        ignoreDuplicateNames?: boolean;
      }
    >({
      query: ({ dashboardId, ignoreDuplicateNames, ...body }) => ({
        url: buildEndpoint(ApiEndpoints.DASHBOARD_DUPLICATE, { dashboardId }),
        method: "POST",
        params: { ignoreDuplicateNames },
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Dashboard"]),
    }),

    // Per-Tab Layout Management
    getTabLayout: builder.query<
      TabLayoutResponse,
      { dashboardId: string; tabId: string }
    >({
      query: ({ dashboardId, tabId }) => ({
        url: buildEndpoint(ApiEndpoints.DASHBOARD_TAB_LAYOUT, {
          dashboardId,
          tabId,
        }),
      }),

      providesTags: (_, __, { tabId }) => [
        { type: "DashboardLayout", id: tabId },
      ],
    }),

    saveTabLayout: builder.mutation<
      void,
      {
        dashboardId: string;
        tabId: string;
        name: string;
        layoutData: DashboardLayoutData;
      }
    >({
      query: ({ dashboardId, tabId, name, layoutData }) => ({
        url: buildEndpoint(ApiEndpoints.DASHBOARD_TAB_BY_ID, { tabId }),
        method: "PATCH",
        body: {
          name,
          content: JSON.stringify(layoutData),
        },
      }),
      invalidatesTags: (_, error, { tabId }) =>
        error ? [] : [{ type: "DashboardLayout", id: tabId }],
    }),

    deleteTab: builder.mutation<
      void,
      { tabId: string; forceDelete: boolean; dashboardId?: string }
    >({
      query: ({ tabId, forceDelete }) => ({
        url: buildEndpoint(ApiEndpoints.DASHBOARD_TAB_BY_ID, { tabId }),
        method: "DELETE",
        params: { forceDelete },
      }),
      // Invalidate to refetch dashboard without the deleted tab
      invalidatesTags: (_, error, { dashboardId }) =>
        error
          ? []
          : dashboardId
            ? [{ type: "Dashboard", id: dashboardId }]
            : [],
    }),

    createDashboardTab: builder.mutation<
      { id: string; name: string; displayOrder: number; groupName?: string | null },
      { dashboardId: string; name: string }
    >({
      query: ({ dashboardId, name }) => ({
        url: buildEndpoint(ApiEndpoints.DASHBOARD_TABS, { dashboardId }),
        method: "POST",
        body: { name },
      }),
      // Invalidate to refetch dashboard with real tab IDs
      invalidatesTags: (_, error, { dashboardId }) =>
        error ? [] : [{ type: "Dashboard", id: dashboardId }],
    }),

    duplicateDashboardTab: builder.mutation<
      { id: string; name: string; displayOrder: number; groupName?: string | null },
      { dashboardId: string; tabId: string; name: string }
    >({
      query: ({ dashboardId, tabId, name }) => ({
        url: buildEndpoint(ApiEndpoints.DASHBOARD_TAB_DUPLICATE, {
          dashboardId,
          tabId,
        }),
        method: "POST",
        body: { tabName: name },
      }),
      // Invalidate to refetch dashboard with the duplicated tab
      invalidatesTags: (_, error, { dashboardId }) =>
        error ? [] : [{ type: "Dashboard", id: dashboardId }],
    }),

    // Bulk update tab group/order in one atomic call
    bulkUpdateTabs: builder.mutation<
      void,
      { dashboardId: string; tabsToUpdate: TabBulkUpdateItem[] }
    >({
      query: ({ dashboardId, tabsToUpdate }) => ({
        url: buildEndpoint(ApiEndpoints.DASHBOARD_TABS, { dashboardId }),
        method: "PATCH",
        body: { tabsToUpdate },
      }),
      invalidatesTags: (_, error, { dashboardId }) =>
        error ? [] : [{ type: "Dashboard", id: dashboardId }],
    }),

    // Update tab name
    updateTabName: builder.mutation<void, { tabId: string; name: string }>({
      query: ({ tabId, name }) => ({
        url: buildEndpoint(ApiEndpoints.DASHBOARD_TAB_BY_ID, { tabId }),
        method: "PATCH",
        body: { name },
      }),
      // Invalidate dashboard data to refresh tab names
      invalidatesTags: ["Dashboard"],
    }),

    lockDashboard: builder.mutation<
      { lockedBy: string; lockedAt: string },
      string
    >({
      query: (dashboardId) => ({
        url: buildEndpoint(ApiEndpoints.DASHBOARD_LOCK, { dashboardId }),
        method: "POST",
      }),
      invalidatesTags: ["Dashboard"],
    }),

    unlockDashboard: builder.mutation<
      void,
      { dashboardId: string; force?: boolean }
    >({
      query: ({ dashboardId, force }) => ({
        url: buildEndpoint(ApiEndpoints.DASHBOARD_LOCK, { dashboardId }),
        method: "DELETE",
        params: force ? { force: true } : undefined,
      }),
      invalidatesTags: ["Dashboard"],
    }),

    // Get dashboard filters
    getDashboardFilters: builder.query<DashboardFiltersResponse, string>({
      query: (dashboardId) => ({
        url: buildEndpoint(ApiEndpoints.DASHBOARD_FILTERS, { dashboardId }),
      }),
      providesTags: (_, __, dashboardId) => [
        { type: "Dashboard", id: `${dashboardId}-filters` },
      ],
    }),
    validateGuardrails: builder.mutation<ValidateGuardrailsResponse, ValidateGuardrailsRequest>({
      query: (body) => ({
        url: ApiEndpoints.DASHBOARD_WIDGETS_VALIDATE,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useLockDashboardMutation,
  useUnlockDashboardMutation,
  useGetDashboardsQuery,
  useCreateDashboardMutation,
  useDuplicateDashboardMutation,
  useGetDashboardByIdQuery,
  usePatchDashboardMutation,
  useGetClientsQuery,
  useGetTabLayoutQuery,
  useSaveTabLayoutMutation,
  useDeleteTabMutation,
  useCreateDashboardTabMutation,
  useDuplicateDashboardTabMutation,
  useBulkUpdateTabsMutation,
  useUpdateTabNameMutation,
  useGetDashboardClientsQuery,
  useShareDashboardWithUsersMutation,
  useRevokeDashboardAccessMutation,
  useGetDashboardFiltersQuery,
  useValidateGuardrailsMutation,
} = dashboardApi;
export default dashboardApi;
